/**
 * usePatientList Hook
 *
 * Business logic for patient list view including:
 * - Fetching patients with pagination
 * - Search functionality with debouncing
 * - Payer / coverage-bucket filtering (mutually exclusive with search)
 * - Event tracking
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessToken } from '@features/user';
import { useEventTracking } from '@services/statsig';
import debounce from 'lodash/debounce';

import APIService from '../../../services/APIService';
import SentryService from '../../../services/SentryService';
import type { Patient } from '../../../types/types';
import type { CoverageBucket, PatientListFilter, PatientsResponse } from '../types';
import type {
  PatientRequirementBlockDto,
  RequirementColumnView,
} from '../utils/requirementColumn';
import { columnsForActiveFilter, toRequirementColumn } from '../utils/requirementColumn';

/** The definition a rail row hands over when it is clicked. */
export interface RequirementFilterTarget {
  id: string;
  label: string;
  kind: string;
  /** The count shown on the row at the moment of the click. */
  count?: number;
}

/** Where a filter was cleared from, for the abandon half of the funnel. */
export type FilterClearSource = 'rail_row' | 'chip';

export interface UsePatientListOptions {
  initialPageSize?: number;
}

export interface UsePatientListReturn {
  patients: Patient[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isEmpty: boolean;
  isSearchActive: boolean;
  activeFilter: PatientListFilter;
  setSearchTerm: (term: string) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  clearSearch: () => void;
  setPayerFilter: (payerUuid: string, payerName: string) => void;
  setCoverageBucketFilter: (bucket: CoverageBucket) => void;
  setRequirementFilter: (target: RequirementFilterTarget) => void;
  clearFilter: (source?: FilterClearSource) => void;
  /**
   * Each patient's standing on the filtered requirement, keyed by patient id.
   * Empty unless a requirement filter is active — the unfiltered response
   * carries no requirement block at all.
   */
  requirementColumns: Record<string, RequirementColumnView>;
  refresh: () => void;
  navigateToPatient: (patientId: string) => void;
  handlePatientCreated: (patientId: string) => void;
}

export function usePatientList(options: UsePatientListOptions = {}): UsePatientListReturn {
  const { initialPageSize = 20 } = options;

  const { getAccessToken } = useAccessToken();
  const navigate = useNavigate();
  const {
    patient: patientMetrics,
    search: searchMetrics,
    patientRequirementsRail,
  } = useEventTracking();

  // State
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTermState] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activeFilter, setActiveFilter] = useState<PatientListFilter>(null);
  /**
   * The columns, and the definition they describe.
   *
   * Keeping the definition id alongside them is the whole fix: columns are
   * rendered only while they still describe the filter that is active. Holding
   * a bare map meant the previous filter's statuses stayed on screen until the
   * new request returned — rows showing one requirement's state under another
   * requirement's chip — and a late response from the OLD filter could repaint
   * them even after the new one had landed.
   */
  const [columnsByFilter, setColumnsByFilter] = useState<{
    definitionId: string | null;
    columns: Record<string, RequirementColumnView>;
  }>({ definitionId: null, columns: {} });

  // Refs for tracking
  const abortControllerRef = useRef<AbortController | null>(null);
  const isInitialFetch = useRef(true);
  /**
   * Which fetch is the current one.
   *
   * The AbortController above cannot actually cancel anything —
   * `APIService.makeAPIGetRequest` takes no signal — so an in-flight request
   * for a filter the user has left still resolves and still writes its result.
   * This counter is what makes a superseded response harmless: it is compared
   * on the way out, and anything that is not the latest is dropped.
   *
   * Guarding the columns alone was not enough (Greptile, #2934): a late
   * response still replaced the PATIENTS, so the previous filter's rows sat
   * under the current chip with their statuses correctly withheld — which
   * reads as "these patients have no requirement" rather than as a stale list.
   * Rows and columns have to be accepted or rejected together.
   */
  const requestSeqRef = useRef(0);

  /**
   * Retire whatever is in flight, now.
   *
   * Bumping the counter only when the REPLACEMENT fetch starts leaves a window
   * (Greptile, #2934): the fetch is issued by an effect, so between the state
   * change and the effect running an obsolete response can resolve and still
   * match the current sequence. Invalidate first, fetch later — the same
   * one-frame window that needed closing on #2892, where a ref was written in
   * an effect and read by a callback that could settle before it.
   *
   * Applied to every setter that changes what is being asked for, not just the
   * requirement filter: the guard is one mechanism, and a version that
   * protected one filter and not its neighbours would be the more confusing
   * thing to maintain.
   */
  const invalidateInFlight = useCallback(() => {
    requestSeqRef.current += 1;
  }, []);

  const fetchPatients = useCallback(
    async (search: string = '', pageNum: number, pageSizeNum: number, filter: PatientListFilter = null) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const seq = ++requestSeqRef.current;
      const isCurrent = () => seq === requestSeqRef.current;

      setIsLoading(true);
      setError(null);

      try {
        let endpoint: string;
        if (search) {
          // Search mode — filters are not compatible with search
          endpoint = `/patient/search?q=${encodeURIComponent(search)}&page=${pageNum + 1}&per_page=${pageSizeNum}`;
        } else {
          endpoint = `/patient/list?page=${pageNum + 1}&per_page=${pageSizeNum}`;
          if (filter?.type === 'payer') {
            endpoint += `&payer_uuid=${encodeURIComponent(filter.payer_uuid)}`;
          } else if (filter?.type === 'coverage_bucket') {
            endpoint += `&coverage_bucket=${encodeURIComponent(filter.bucket)}`;
          } else if (filter?.type === 'requirement') {
            // GDN-3423. With this parameter every row also carries a
            // `patient_requirement` block; without it the response is unchanged.
            endpoint += `&patient_requirement_definition_id=${encodeURIComponent(
              filter.patient_requirement_definition_id,
            )}`;
          }
        }

        const response = await APIService.makeAPIGetRequest({
          requestString: endpoint,
          accessToken: await getAccessToken(),
        });

        if (response.ok) {
          const data = (await response.value) as PatientsResponse & { total_pages?: number };
          // A newer fetch has started; this answer is about a filter the user
          // has already left.
          if (!isCurrent()) return;
          setPatients(data.patients);

          // Built from the response rather than held across fetches: a stale
          // column beside a fresh row would show one patient's requirement
          // against another's name.
          const columns: Record<string, RequirementColumnView> = {};
          for (const patient of data.patients) {
            const block = (patient as { patient_requirement?: PatientRequirementBlockDto })
              .patient_requirement;
            if (block) {
              columns[patient.patient_id] = toRequirementColumn(block);
            }
          }
          setColumnsByFilter({
            definitionId:
              filter?.type === 'requirement'
                ? filter.patient_requirement_definition_id
                : null,
            columns,
          });

          setTotalRows(data.total);
          // API may return totalPages or total_pages, calculate as fallback
          const pages = data.totalPages || data.total_pages || Math.ceil(data.total / pageSizeNum);
          setTotalPages(pages);

          // Track search results or list view
          if (search) {
            searchMetrics.performed({
              search_type: 'patient',
              search_context: 'list_page',
              query_length: search.length,
              results_count: data.total,
              has_results: data.total > 0,
            });
          } else if (!isInitialFetch.current) {
            patientMetrics.listViewed({
              total_count: data.total,
              filtered: filter !== null,
              page: pageNum + 1,
              page_size: pageSizeNum,
            });
          }

          isInitialFetch.current = false;
        } else {
          throw new Error('Failed to fetch patients');
        }
      } catch (err) {
        // A late FAILURE must not blank the list either: the request that
        // failed is not the one on screen.
        if (!isCurrent()) return;
        if ((err as Error).name !== 'AbortError') {
          console.error('Error fetching patients:', err);
          setError('Something went wrong while loading patients. Please try again.');

          // Log to Sentry for production monitoring
          SentryService.captureException(err, {
            level: 'error',
            tags: {
              component: 'usePatientList',
              feature: 'patients',
              operation: search ? 'search' : 'list',
            },
            extra: {
              method: 'fetchPatients',
              endpoint: search
                ? `/patient/search?q=${search}`
                : '/patient/list',
              search_query: search || null,
              page: pageNum,
              page_size: pageSizeNum,
              filter_type: filter?.type ?? null,
            },
          });

          setPatients([]);
          setTotalRows(0);
          setTotalPages(0);
        }
      } finally {
        // Only the current request may clear the spinner; a superseded one
        // finishing would say the in-flight fetch had finished too.
        if (isCurrent()) setIsLoading(false);
      }
    },
    [getAccessToken, patientMetrics, searchMetrics],
  );

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((search: string, pageNum: number, pageSizeNum: number, filter: PatientListFilter) => {
      void fetchPatients(search, pageNum, pageSizeNum, filter);
    }, 300),
    [fetchPatients],
  );

  // Effect to fetch on changes
  useEffect(() => {
    if (searchTerm) {
      debouncedFetch(searchTerm, page, pageSize, null);
    } else {
      void fetchPatients(searchTerm, page, pageSize, activeFilter);
    }

    return () => {
      debouncedFetch.cancel();
    };
  }, [searchTerm, page, pageSize, activeFilter, fetchPatients, debouncedFetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ── Actions ──────────────────────────────────────────────────

  const setSearchTerm = useCallback((term: string) => {
    invalidateInFlight();
    setSearchTermState(term);
    setPage(0);
    // Search and filter are mutually exclusive
    if (term) {
      setActiveFilter(null);
    }
  }, [invalidateInFlight]);

  const clearSearch = useCallback(() => {
    invalidateInFlight();
    if (searchTerm) {
      searchMetrics.cleared({
        search_type: 'patient',
        search_context: 'list_page',
        had_results: patients.length > 0,
        query_length: searchTerm.length,
      });
    }
    setSearchTermState('');
    setPage(0);
  }, [searchTerm, patients.length, searchMetrics, invalidateInFlight]);

  const setPayerFilter = useCallback((payerUuid: string, payerName: string) => {
    invalidateInFlight();
    setActiveFilter({ type: 'payer', payer_uuid: payerUuid, payer_name: payerName });
    setSearchTermState('');
    setPage(0);
  }, [invalidateInFlight]);

  const setCoverageBucketFilter = useCallback((bucket: CoverageBucket) => {
    invalidateInFlight();
    setActiveFilter({ type: 'coverage_bucket', bucket });
    setSearchTermState('');
    setPage(0);
  }, [invalidateInFlight]);

  const setRequirementFilter = useCallback(
    (target: RequirementFilterTarget) => {
      invalidateInFlight();
      patientRequirementsRail.filterSelected({
        patient_requirement_definition_id: target.id,
        // The kind, never the label: the label is practice-authored text that
        // patients read.
        kind: target.kind,
        missing_count: target.count,
      });
      setActiveFilter({
        type: 'requirement',
        patient_requirement_definition_id: target.id,
        requirement_label: target.label,
        requirement_kind: target.kind,
      });
      setSearchTermState('');
      setPage(0);
    },
    [patientRequirementsRail, invalidateInFlight],
  );

  const clearFilter = useCallback(
    (source?: FilterClearSource) => {
      invalidateInFlight();
      // Only the requirement filter reports its own clear; the payer filter
      // has never had one, and inventing events for it here would be scope
      // this issue does not own.
      if (activeFilter?.type === 'requirement') {
        patientRequirementsRail.clearClicked({
          patient_requirement_definition_id: activeFilter.patient_requirement_definition_id,
          kind: activeFilter.requirement_kind,
          source,
        });
      }
      setActiveFilter(null);
      setPage(0);
    },
    [activeFilter, patientRequirementsRail, invalidateInFlight],
  );

  const refresh = useCallback(() => {
    void fetchPatients(searchTerm, page, pageSize, activeFilter);
  }, [fetchPatients, searchTerm, page, pageSize, activeFilter]);

  const navigateToPatient = useCallback(
    (patientId: string) => {
      patientMetrics.selected(patientId, {
        source: 'list',
        context: 'viewing',
      });
      navigate(`/patient/${patientId}`);
    },
    [navigate, patientMetrics],
  );

  const handlePatientCreated = useCallback(
    (_patientId: string) => {
      refresh();
    },
    [refresh],
  );

  return {
    patients,
    isLoading,
    error,
    searchTerm,
    page,
    pageSize,
    totalRows,
    totalPages,
    hasNextPage: page < totalPages - 1,
    hasPreviousPage: page > 0,
    isEmpty: !isLoading && patients.length === 0,
    isSearchActive: searchTerm.length > 0,
    activeFilter,
    setSearchTerm,
    setPage,
    setPageSize,
    clearSearch,
    setPayerFilter,
    setCoverageBucketFilter,
    setRequirementFilter,
    clearFilter,
    // Withheld the moment the filter changes, rather than lingering until the
    // next response lands. An empty column is honest; a stale one is not.
    requirementColumns: columnsForActiveFilter(
      activeFilter?.type === 'requirement'
        ? activeFilter.patient_requirement_definition_id
        : null,
      columnsByFilter,
    ),
    refresh,
    navigateToPatient,
    handlePatientCreated,
  };
}
